import { Injectable } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { environment } from '../../../environments/environment';

export interface ExtendedMenuItem extends MenuItem {
  preRelease?: boolean;
  items?: ExtendedMenuItem[];
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private menuItems: ExtendedMenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      routerLink: '/dashboard'
    },
    {
      label: 'DEV',
      icon: 'pi pi-file',
      preRelease: true,
      items: [
        {
          label: 'Jab',
          icon: 'pi pi-calendar',
          routerLink: '/dev/jab'
        },
        {
          label: 'Invoice Generation',
          icon: 'pi pi-file-plus',
          routerLink: '/dev/invoice-generation'
        },
        {
          label: 'Test Login',
          icon: 'pi pi-sign-in',
          routerLink: '/dev/test-login'
        },
        {
          label: 'Test Logout',
          icon: 'pi pi-sign-out',
          routerLink: '/dev/test-logout'
        }
      ]
    },
    {
      label: 'Tenants',
      icon: 'pi pi-users',
      preRelease: false,
      items: [
        {
          label: 'Tenant List',
          icon: 'pi pi-list',
          routerLink: '/tenant/list'
        },
        {
          label: 'New Tenant',
          icon: 'pi pi-user-plus',
          routerLink: '/tenant/new'
        }
      ]
    },
    {
      label: 'Invoices',
      icon: 'pi pi-file',
      preRelease: false,
      items: [
        {
          label: 'View Invoice',
          icon: 'pi pi-search',
          routerLink: '/invoices/view'
        },
        {
          label: 'Create Invoice',
          icon: 'pi pi-plus',
          routerLink: '/invoices/single'
        },
        {
          label: 'Invoice List',
          icon: 'pi pi-list',
          routerLink: '/invoices/list'
        },
        {
          label: 'Monthly Invoices',
          icon: 'pi pi-calendar',
          routerLink: '/invoices/listmonthly'
        },
        {
          label: 'Batch Output Invoices',
          icon: 'pi pi-download',
          routerLink: '/invoices/batch-output'
        },
      ]
    },
    {
      label: 'Daily Receipts',
      icon: 'pi pi-file+plus',
      preRelease: false,
      items: [
        {
          label: 'Daily Summary Import',
          icon: 'pi pi-upload',
          routerLink: '/parking-device/daily-summary-import'
        }
      ]
    },
    {
      label: 'SUREPARK',
      icon: 'pi pi-car',
      items: [
        {
          label: 'Coupon Billing',
          icon: 'pi pi-ticket',
          routerLink: '/surepark/invoice/coupons'
        },
        {
          label: 'Configuration',
          icon: 'pi pi-cog',
          routerLink: '/surepark/config'
        },
        {
          label: 'Account Validity',
          icon: 'pi pi-check-circle',
          routerLink: '/surepark/accounts-validity-update'
        },
        {
          label: 'Terminate Contacts',
          icon: 'pi pi-times-circle',
          routerLink: '/surepark/terminate-contacts'
        },
        {
          label: 'Merge Tool',
          icon: 'pi pi-sync',
          routerLink: '/surepark/surepark-merge'
        },
        {
          label: 'Daily Summary Import',
          icon: 'pi pi-upload',
          routerLink: '/surepark/daily-summary-import'
        },
        {
          label: 'Daily Summary Import Config',
          icon: 'pi pi-cog',
          routerLink: '/surepark/daily-summary-import-settings'
        },
        {
          label: 'Member IDs Report',
          icon: 'pi pi-id-card',
          routerLink: '/surepark/member-ids-report'
        },
        {
          label: 'Variance Report',
          icon: 'pi pi-chart-line',
          routerLink: '/surepark/variance-report',
          preRelease: true
        }
      ]
    },
    // The leases tool was canceled before being finished
    // {
    //   label: 'Leases',
    //   icon: 'pi pi-file-pdf',
    //   items: [
    //     {
    //       label: 'Lease List',
    //       icon: 'pi pi-list',
    //       routerLink: '/leases/list'
    //     }
    //   ]
    // },
    {
      label: 'Reports',
      icon: 'pi pi-chart-bar',
      items: [
        {
          label: 'Run Reports',
          icon: 'pi pi-play',
          routerLink: '/reports/run'
        },
        {
          label: 'Performance Metrics',
          icon: 'pi pi-chart-line',
          routerLink: '/performance-metrics'
        }
      ]
    },
    {
      label: 'Settings - Location',
      icon: 'pi pi-cog',
      items: [
        {
          label: 'Daily Summary Configuration',
          icon: 'pi pi-sliders-h',
          routerLink: '/parking-device/daily-summary-import-settings'
        },
        {
          label: 'SUREPARK Connection Settings',
          icon: 'pi pi-sliders-h',
          routerLink: '/lot/settings',
          allowedRoles: ["Portal Admin"]
        },
        {
          label: 'Lot Settings',
          icon: 'pi pi-cog',
          routerLink: '/settings/lot-settings'
        },
        {
          label: 'Sequence Management',
          icon: 'pi pi-sort-numeric-up',
          routerLink: '/sequence-management'
        },
        {
          label: 'Rate Change Tool',
          icon: 'pi pi-dollar',
          routerLink: '/settings/update-rate'
        },
        {
          label: 'Version Information',
          icon: 'pi pi-info-circle',
          routerLink: '/version'
        }
      ]
    },
    {
      label: 'Admin',
      icon: 'pi pi-shield',
      allowedRoles: ["Portal Admin"],
      items: [
        {
          label: 'Logs - Drupal',
          icon: 'pi pi-history',
          routerLink: '/admin/watchdog-logs'
        }
      ]
    },
    {
      label: 'Old Attendant Portal',
      icon: 'pi pi-sign-out',
      url: environment.SITE_URL
    }
  ];

  getMenuItems(hasPreReleaseAccess: boolean = false, userRole: string): ExtendedMenuItem[] {
    let items = []; 
    if (hasPreReleaseAccess) {
      items = this.menuItems;
    } else {
      items = this.filterPreReleaseItems(this.menuItems);
    }

    return this.filterRoleBasedItems(items, userRole);
  }

  private filterPreReleaseItems(items: ExtendedMenuItem[]): ExtendedMenuItem[] {
    return items
      .filter(item => !item.preRelease)
      .map(item => {
        if (item.items) {
          return { ...item, items: this.filterPreReleaseItems(item.items) };
        }
        return item;
      });
  }

  private filterRoleBasedItems(items: ExtendedMenuItem[], userRole: string): ExtendedMenuItem[] {
    
    return items
      .filter(item => !item['allowedRoles'] || item['allowedRoles']?.includes(userRole))
      .map(item => {
        if (item.items) {
          return { ...item, items: this.filterRoleBasedItems(item.items, userRole) };
        }
        return item;
      });
  }
}
